import { Component, computed, signal } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import { EntitySelectorComponent } from '../../entity-selector/entity-selector.component'
import { FormsModule } from '@angular/forms'
import { ScheduleMDMASettlementRunEvent } from '../../scheduleing/scheduling.service'
import { EntityRef } from 'src/app/shared/models/referenceData'
import { DateRangeFilterPickerComponent } from '../../date-range-filter-picker/date-range-filter-picker.component'
import { DateRangeFilterStateValue } from 'src/app/shared/services/filter/date-range-filter'
import { isAfter, isBefore } from 'date-fns'
import { stl } from 'src/app/shared/utilities/date'

interface ModalState {
  settlement_type: EntityRef
  event_timestamp: DateRangeFilterStateValue
  operating_date: DateRangeFilterStateValue
}


@Component({
  imports: [
    FormsModule,
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    LabeledPropertyComponent,
    DateRangeFilterPickerComponent,
    EntitySelectorComponent,
  ],
  selector: 'app-schedule-mdma-settlements-run-modal',
  styles: `
  .error {
    color: red;
    font-size: 0.9rem;
  }`,
  template: `
    <div app-modal-header>Schedule MDMA Run</div>

    <div app-modal-body>
      <div app-labeled-property label="Operating Day">
          <div dateRangeFilterPicker
            [value]="modalState().operating_date"
            (valueChange)="updateOperatingDate($event)"
            [showOperation]="false"
            [showRange]="false"
            [showTime]="false"
            [showSeconds]="false"
          ></div>
      </div>
      <div app-labeled-property label="Settlement Type">
        <div
          app-entity-selector
          [slim]="true"
          label="Settlement Type"
          [config]="{
            resource: 'mdmaSettlementTypes', 
            allowMultiple: false
          }"
          [value]="modalState().settlement_type"
          (valueChange)="updateSettlementType($event)"
        ></div>
      </div>
      <div app-labeled-property label="Scheduled Time">
          <div dateRangeFilterPicker
            [value]="modalState().event_timestamp"
            (valueChange)="updateEventTimestamp($event)"
            [showOperation]="false"
            [showRange]="false"
            [showTime]="true"
            [showSeconds]="false"
            [viewAsTZ]="'America/Chicago'"
          ></div>
      </div>
    </div>

    <div app-modal-footer>
      @if(this.error()){<span class="error">{{error()}}</span>}
      <button
        class="action-btn primary"
        [disabled]="!canSchedule()"
        (click)="schedule()"
      >
        Schedule
      </button>
      <button class="action-btn" (click)="onCancel()()">Cancel</button>
    </div>
  `,
})
export class ScheduleMDMASettlementsRunModalComponent {
  private readonly SCHEDULED_IN_PAST_ERROR = 'The selected schedule date is in the past, please select a schedule date in the future'

  modalState = signal<Partial<ModalState>>({})
  onSchedule = signal<(evt: ScheduleMDMASettlementRunEvent) => void>(() => void 0)
  onCancel = signal<() => void>(() => void 0)
  error = signal<string | undefined>(undefined)

  canSchedule = computed(() => {
    const state = this.modalState()
    return state.event_timestamp && 
      state.operating_date &&
      state.settlement_type &&
      isAfter(state.event_timestamp.value as Date, stl())
  })

  init(overrides: Partial<{
    modalState: Partial<ModalState>,
    onSchedule: (evt: ScheduleMDMASettlementRunEvent) => void,
    onCancel: () => void
  }>) {
    this.modalState.update(v => overrides.modalState ?? v)
    this.onSchedule.update(v => overrides.onSchedule ?? v)
    this.onCancel.update(v => overrides.onCancel ?? v)
  }

  updateOperatingDate(operating_date: DateRangeFilterStateValue){
    this.modalState.update(v => ({ ...v, operating_date }))
  }
  updateEventTimestamp(event_timestamp: DateRangeFilterStateValue){
    if(isBefore(event_timestamp.value as Date, stl())) {
      this.error.set(this.SCHEDULED_IN_PAST_ERROR)
    } else {
      this.error.set(undefined)
    }
    this.modalState.update(v => ({ ...v, event_timestamp }))
  }
  updateSettlementType(settlement_type: EntityRef){
    this.modalState.update(v => ({ ...v, settlement_type }))
  }
  schedule() {
    if(isBefore(
      this.modalState().event_timestamp?.value as Date, 
      stl(),
    )) {
      this.error.set(this.SCHEDULED_IN_PAST_ERROR)
    } else {
      this.error.set(undefined)
      const state = this.modalState()
      this.onSchedule()({
        category: 'mdma_aggregation',
        event_timestamp: state.event_timestamp!.value as Date,
        settlement_type: state.settlement_type!.entity_code,
        operating_date: state.operating_date!.value as Date,
      })
    }  
  }
}
